import React from "react";
import { Box, useTheme } from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { observer } from "mobx-react-lite";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { alertsStore } from "../../../stores/alerts/AlertsStore";
import { SectionIcon, getAlertColors } from "./alertUi";

interface SectionPickerProps {
  value: string | null;
  onChange: (section: string | null) => void;
  sx?: object;
}

const CountBadge: React.FC<{
  count: number;
  high: boolean;
  active: boolean;
}> = ({ count, high, active }) => {
  const theme = useTheme();
  const colors = getAlertColors(theme);
  const c = theme.palette.customColors;
  if (count <= 0) return null;
  return (
    <Box
      component="span"
      sx={{
        minWidth: 18,
        height: 18,
        px: 0.6,
        borderRadius: "999px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.65rem",
        fontWeight: 700,
        lineHeight: 1,
        color: high || active ? c.white : colors.accent,
        bgcolor: high ? c.errorRed : active ? colors.accent : colors.unreadBg,
      }}
    >
      {count > 99 ? "99+" : count}
    </Box>
  );
};

/** Horizontal, scrollable section filter (navbar panel and small screens). */
export const SectionChips: React.FC<SectionPickerProps> = observer(
  ({ value, onChange, sx }) => {
    const theme = useTheme();
    const colors = getAlertColors(theme);
    const sections = alertsStore.sections;
    if (sections.length === 0) return null;

    const chip = (
      key: string,
      label: string,
      active: boolean,
      count: number,
      high: boolean,
      onClick: () => void,
    ) => (
      <Box
        key={key}
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          height: 28,
          px: 1.25,
          flexShrink: 0,
          cursor: "pointer",
          borderRadius: "999px",
          fontFamily: "inherit",
          fontSize: "0.75rem",
          fontWeight: 600,
          whiteSpace: "nowrap",
          color: active ? colors.accent : colors.subtitle,
          bgcolor: active ? colors.unreadBg : colors.surface,
          border: `1px solid ${active ? colors.accent : colors.border}`,
          transition: "all 0.15s ease",
          "&:hover": { borderColor: colors.accent, color: colors.accent },
        }}
      >
        {label}
        <CountBadge count={count} high={high} active={active} />
      </Box>
    );

    return (
      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          overflowX: "auto",
          pb: 0.25,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          ...sx,
        }}
      >
        {chip(
          "__all",
          "All",
          value == null,
          alertsStore.totalUnread,
          alertsStore.totalHighUnread > 0,
          () => onChange(null),
        )}
        {sections.map((section) =>
          chip(
            section.code,
            section.label,
            value === section.code,
            section.unread,
            section.high_unread > 0,
            () => onChange(section.code),
          ),
        )}
      </Box>
    );
  },
);

/** Vertical section list for the inbox page sidebar. */
export const SectionRail: React.FC<SectionPickerProps> = observer(
  ({ value, onChange, sx }) => {
    const theme = useTheme();
    const colors = getAlertColors(theme);

    const item = (
      key: string,
      label: string,
      icon: React.ReactNode,
      active: boolean,
      unread: number,
      high: boolean,
      open: number | null,
      onClick: () => void,
    ) => (
      <Box
        key={key}
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.25,
          py: 1,
          border: 0,
          cursor: "pointer",
          textAlign: "left",
          borderRadius: "10px",
          fontFamily: "inherit",
          bgcolor: active ? colors.unreadBg : "transparent",
          color: active ? colors.accent : colors.text,
          transition: "background-color 0.15s ease",
          "&:hover": { bgcolor: active ? colors.unreadBg : colors.hover },
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: active ? colors.accent : colors.subtitle,
            bgcolor: active ? colors.surface : colors.surfaceMuted,
            border: `1px solid ${active ? colors.accent : colors.border}`,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CustomTypography
            variant="body1"
            color={active ? colors.accent : colors.title}
            fontWeight={active ? 700 : 600}
            sx={{
              fontSize: "0.82rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </CustomTypography>
          {open != null && open > 0 && (
            <CustomTypography variant="caption" color={colors.subtitle}>
              {open} open
            </CustomTypography>
          )}
        </Box>
        <CountBadge count={unread} high={high} active={active} />
      </Box>
    );

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, ...sx }}>
        {item(
          "__all",
          "All sections",
          <InboxOutlinedIcon sx={{ fontSize: 18 }} />,
          value == null,
          alertsStore.totalUnread,
          alertsStore.totalHighUnread > 0,
          null,
          () => onChange(null),
        )}
        {alertsStore.sections.map((section) =>
          item(
            section.code,
            section.label,
            <SectionIcon icon={section.icon} section={section.code} />,
            value === section.code,
            section.unread,
            section.high_unread > 0,
            section.open,
            () => onChange(section.code),
          ),
        )}
      </Box>
    );
  },
);
